(() => {
  const existing = window.MenoPlayPdpHero;
  if (existing) {
    existing.init(document);
    return;
  }

  const controllers = new Map();

  const destroy = (id) => {
    controllers.get(id)?.abort();
    controllers.delete(id);
  };

  const initRoot = (root) => {
    const id = root.dataset.sectionId;
    if (!id) return;

    destroy(id);
    const controller = new AbortController();
    const { signal } = controller;
    controllers.set(id, controller);
    const on = (target, type, listener) => target?.addEventListener(type, listener, { signal });

    const main = root.querySelector('[data-gallery-main]');
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const tiles = root.querySelector('.menoplay-pdp-pdp-gallery__tiles');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let galleryIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.matches('.is-active, .menoplay-pdp-is-active')));
    const revealThumb = (thumb) => {
      thumb?.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    };
    const setActiveThumb = (index) => {
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.classList.toggle('menoplay-pdp-is-active', active);
        thumb.toggleAttribute('aria-current', active);
      });
    };
    const showMedia = (next) => {
      if (!main || !thumbs.length) return;
      galleryIndex = (next + thumbs.length) % thumbs.length;
      const selected = thumbs[galleryIndex];
      const source = selected.dataset.gallerySrc;
      if (!source) return;
      main.removeAttribute('srcset');
      main.src = source;
      main.alt = selected.dataset.galleryAlt || selected.getAttribute('aria-label') || main.alt;
      setActiveThumb(galleryIndex);
      revealThumb(selected);
    };

    thumbs.forEach((thumb, index) => on(thumb, 'click', () => showMedia(index)));
    on(root.querySelector('[data-gallery-prev]'), 'click', () => showMedia(galleryIndex - 1));
    on(root.querySelector('[data-gallery-next]'), 'click', () => showMedia(galleryIndex + 1));
    on(tiles, 'keydown', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (!thumb || !tiles.contains(thumb)) return;

      let nextIndex;
      if (event.key === 'ArrowLeft') nextIndex = galleryIndex - 1;
      else if (event.key === 'ArrowRight') nextIndex = galleryIndex + 1;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = thumbs.length - 1;
      else return;

      event.preventDefault();
      showMedia(nextIndex);
      thumbs[galleryIndex]?.focus({ preventScroll: true });
    });
    setActiveThumb(galleryIndex);

    const variantSelect = root.querySelector('[data-menoplay-pdp-variant-select]');
    const getProductForm = () => {
      const buyButton = root.querySelector('[data-add-to-cart], form[data-product-form] button[type="submit"], form[action*="/cart/add"] button[type="submit"]');
      return buyButton?.form || root.querySelector('form[data-product-form], form[action*="/cart/add"]');
    };
    const syncVariant = () => {
      const option = variantSelect?.selectedOptions[0];
      const variantInput = getProductForm()?.querySelector('input[name="id"]');
      if (!option || !variantInput) return;
      variantInput.value = option.value;
      variantInput.dispatchEvent(new Event('input', { bubbles: true }));
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const sellingPlanMirrorSelector = 'input[data-menoplay-selling-plan-mirror]';
    const purchaseOptions = root.querySelector('.menoplay-us-purchase-options');
    const oneTimePromotionalPrice = purchaseOptions?.dataset.oneTimePromotionalPrice?.trim() || '';
    const defaultButtonPrices = new WeakMap();
    on(purchaseOptions, 'click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const option = target?.closest('.appstle_subscription_wrapper_option:not(.appstle_include_dropdown)');
      if (!option || !purchaseOptions.contains(option)) return;

      const radio = option.querySelector('input[type="radio"][name="selling_plan"]');
      const nativeLabel = target?.closest('label');
      if (!radio || target === radio || (nativeLabel && (nativeLabel.htmlFor === radio.id || nativeLabel.contains(radio)))) return;

      if (!radio.checked) radio.click();
    });
    const getSelectedExternalPlan = () => [...(purchaseOptions?.querySelectorAll('input[name="selling_plan"]') || [])]
      .find((input) => input.checked && input.form === null);
    const getOptionAmount = (input) => {
      let option = input;
      while (option && option !== purchaseOptions) {
        const amount = option.querySelector?.('.appstle_subscription_amount');
        if (amount?.textContent.trim()) return amount.textContent.trim();
        option = option.parentElement;
      }
      return '';
    };
    const formatAgainst = (source, target) => {
      const sourceToken = source.match(/\d+(?:[.,]\d+)?/)?.[0];
      const targetToken = target.match(/\d+(?:[.,]\d+)?/)?.[0];
      if (!sourceToken || !targetToken) return target;

      const [whole, fraction] = sourceToken.split(/[.,]/);
      const numericAmount = fraction && /[1-9]/.test(fraction)
        ? `${whole}${targetToken.includes(',') ? ',' : '.'}${fraction}`
        : whole;
      return target.replace(targetToken, numericAmount);
    };
    const renderOneTimePromotionalPrice = () => {
      if (!oneTimePromotionalPrice) return;
      const amount = purchaseOptions?.querySelector('.appstle_subscription_wrapper_option:not(.appstle_include_dropdown) .appstle_subscription_amount');
      if (!amount || amount.querySelector('.menoplay-one-time-price__promotional')) return;

      const originalPrice = amount.textContent.trim();
      if (!originalPrice) return;
      amount.replaceChildren();

      const original = document.createElement('s');
      original.className = 'menoplay-one-time-price__original';
      original.textContent = originalPrice;
      const promotional = document.createElement('span');
      promotional.className = 'menoplay-one-time-price__promotional';
      promotional.textContent = formatAgainst(oneTimePromotionalPrice, originalPrice);
      amount.append(original, promotional);
    };
    const syncButtonPrice = () => {
      if (!purchaseOptions) return;
      renderOneTimePromotionalPrice();

      const buttonPrice = root.querySelector('[data-button-price]');
      if (!buttonPrice) return;

      if (!defaultButtonPrices.has(buttonPrice)) defaultButtonPrices.set(buttonPrice, buttonPrice.textContent);
      const defaultPrice = defaultButtonPrices.get(buttonPrice);
      const selectedPlan = getSelectedExternalPlan();
      if (!selectedPlan?.value) {
        const nextPrice = oneTimePromotionalPrice ? formatAgainst(oneTimePromotionalPrice, defaultPrice) : defaultPrice;
        if (buttonPrice.textContent !== nextPrice) buttonPrice.textContent = nextPrice;
        return;
      }

      const amount = getOptionAmount(selectedPlan);
      const nextPrice = formatAgainst(amount, defaultPrice);
      if (buttonPrice.textContent !== nextPrice) buttonPrice.textContent = nextPrice;
    };
    const removeSellingPlanMirrors = () => root.querySelectorAll(sellingPlanMirrorSelector).forEach((input) => input.remove());
    const syncSellingPlan = () => {
      if (!document.body.classList.contains('us-subscription-only-view')) return;
      syncButtonPrice();

      const productForm = getProductForm();
      if (!productForm) return;

      const hasNativeSellingPlan = [...productForm.elements].some((element) => (
        element.name === 'selling_plan' && !element.matches(sellingPlanMirrorSelector)
      ));
      if (hasNativeSellingPlan) {
        removeSellingPlanMirrors();
        return;
      }

      const selectedPlan = [...root.querySelectorAll('input[name="selling_plan"]')]
        .find((input) => input.checked && input.form === null);
      if (!selectedPlan?.value) {
        removeSellingPlanMirrors();
        return;
      }

      let mirror = productForm.querySelector(sellingPlanMirrorSelector);
      root.querySelectorAll(sellingPlanMirrorSelector).forEach((input) => {
        if (input !== mirror) input.remove();
      });
      if (!mirror) {
        mirror = document.createElement('input');
        mirror.type = 'hidden';
        mirror.name = 'selling_plan';
        mirror.dataset.menoplaySellingPlanMirror = '';
        productForm.append(mirror);
      }
      if (mirror.value !== selectedPlan.value) mirror.value = selectedPlan.value;
    };
    const sellingPlanObserver = new MutationObserver(syncSellingPlan);
    sellingPlanObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['checked', 'value']
    });
    signal.addEventListener('abort', () => sellingPlanObserver.disconnect(), { once: true });

    on(variantSelect, 'change', () => {
      syncVariant();
      requestAnimationFrame(syncSellingPlan);
    });
    on(root, 'change', (event) => {
      if (event.target.matches('input[name="selling_plan"]')) syncSellingPlan();
    });
    syncVariant();
    syncSellingPlan();
  };

  const init = (scope = document) => {
    if (scope.matches?.('[data-menoplay-pdp-module="hero"]')) initRoot(scope);
    scope.querySelectorAll?.('[data-menoplay-pdp-module="hero"]').forEach(initRoot);
  };

  const onSectionLoad = (event) => init(event.target);
  const onSectionUnload = (event) => {
    const root = event.target.matches?.('[data-menoplay-pdp-module="hero"]') ? event.target : event.target.querySelector?.('[data-menoplay-pdp-module="hero"]');
    if (root?.dataset.sectionId) destroy(root.dataset.sectionId);
  };
  const teardown = () => {
    document.removeEventListener('shopify:section:load', onSectionLoad);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    controllers.forEach((controller) => controller.abort());
    controllers.clear();
    delete window.MenoPlayPdpHero;
  };

  window.MenoPlayPdpHero = { controllers, destroy, init, teardown };
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  init();
})();
